import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import SRText from '@/design-system/helpers/SRText';
import { useBreakpoint } from '@/hooks/useBreakpoint';

import { useSiteSearchContext } from '../../contexts/SiteSearchContext';
import { useSiteSearchState } from '../../hooks/useSiteSearchState';
import { SearchLoading } from '../SearchLoading';
import { SearchRecommendedResultsList } from '../SearchRecommendedResultsList';
import { SearchResultsList } from '../SearchResultsList';
import styles from './SearchContent.module.scss';

/**
 * The SearchContent component contains the search results
 * and other search-related content.
 * @param {object} props - The component props
 * @param {string} props.className - Additional class name
 * @returns {React.Component} The search content component
 */
const SearchContent = ({
    className,
    paginationButton,
    errorComponent,
    noResultsComponent,
    title,
    ...props
}) => {
    const firstResultRef = React.useRef(null);

    const breakpoints = useBreakpoint();
    const {
        coveo: {
            controller: { resultList, querySummary },
        },
        activeFilter,
        facetsState,
        searchValue,
        totalCount,
        CARD_HEADER_HEIGHT,
        results,
        recommendedResults,
        resultsState,
        PAGE_SIZE,
    } = useSiteSearchContext();
    const state = useSiteSearchState();
    const isLoading = (resultsState.isLoading || facetsState.isLoading) && state === 'default';

    /**
     * Handle the more results button click
     * @param {object} e - The event object
     * @returns {void}
     */
    const handleMoreResults = React.useCallback(
        (e) => {
            e.preventDefault();
            resultList.fetchMoreResults();

            // Scroll down after a brief delay when loading more results
            setTimeout(() => {
                if (firstResultRef.current) {
                    // Calculate the position of the next page's first result
                    const elementRect = firstResultRef.current.getBoundingClientRect();
                    const absoluteElementTop = elementRect.top + window.scrollY;

                    // Subtract card header height to correctly position the element in the view
                    const scrollToPosition = absoluteElementTop - CARD_HEADER_HEIGHT;

                    // Scroll to that position
                    window.scrollTo({
                        top: scrollToPosition,
                        behavior: 'smooth',
                    });

                    // Shift focus to the new page's first result
                    firstResultRef.current.focus();
                }
            }, 200);
        },
        [CARD_HEADER_HEIGHT, resultList, firstResultRef],
    );

    return (
        <ContentContainer
            className={classnames(styles['search-content'], className)}
            isFullWidth={!breakpoints?.medium}
            {...props}
        >
            {/* Accessibility Text */}
            <div aria-live="polite" aria-atomic="true">
                {searchValue && (
                    <SRText
                        content={
                            totalCount > 0
                                ? `Search submitted, ${totalCount} results returned for "${searchValue}"`
                                : `No results found for "${searchValue}"`
                        }
                    />
                )}
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles['search-content__loading']}>
                    <SearchLoading />
                </div>
            )}

            {/* Results State */}
            {!isLoading && resultsState.hasResults && (
                <Grid>
                    {/* Recommended Results */}
                    <GridColumn
                        className={styles['search-content__recommended']}
                        colSpan={{ sm: 4, md: 6, lg: 9 }}
                        colStart={{ sm: 1 }}
                    >
                        <SearchRecommendedResultsList
                            results={recommendedResults}
                            activeFilter={activeFilter}
                            analytics={{
                                eventName: 'search',
                                componentName: 'Search Recommended Card',
                                interactionType: 'search_card_cta',
                                searchTerm: searchValue,
                            }}
                        />
                    </GridColumn>

                    {/* Results List */}
                    <GridColumn
                        className={styles['search-content__results']}
                        colSpan={{ sm: 4, md: 6, lg: 9 }}
                        colStart={{ sm: 1 }}
                    >
                        <SearchResultsList
                            results={results}
                            firstResult={querySummary.state.lastResult - PAGE_SIZE + 1}
                            firstResultRef={firstResultRef}
                            analytics={{
                                eventName: 'search',
                                componentName: 'Search Card',
                                interactionType: 'search_card_cta',
                                searchTerm: searchValue,
                            }}
                        />
                    </GridColumn>

                    {/* Pagination Button */}
                    {resultsState.moreResultsAvailable && (
                        <GridColumn
                            className={styles['search-content__more-results']}
                            colSpan={{ sm: 4, md: 3, lg: 3 }}
                            colStart={{ sm: 1, md: 3, lg: 5 }}
                        >
                            <Button
                                {...paginationButton}
                                buttonStyle={Button.STYLE.SECONDARY}
                                onClick={handleMoreResults}
                                csr={true}
                                analytics={{
                                    eventName: 'search',
                                    componentName: 'Search',
                                    componentTitle: title,
                                    searchTerm: searchValue,
                                    interactionType: 'cta',
                                }}
                            />
                        </GridColumn>
                    )}
                </Grid>
            )}

            {/* No Results State */}
            {!isLoading &&
                resultsState.firstSearchExecuted &&
                !resultsState.hasResults &&
                !resultsState.hasError && (
                    <div className={styles['default-search__no-results']}>{noResultsComponent}</div>
                )}

            {/* Error State */}
            {!isLoading && resultsState.hasError && (
                <div className={styles['default-search__error']}>{errorComponent}</div>
            )}
        </ContentContainer>
    );
};

SearchContent.propTypes = {
    className: PropTypes.string,
    paginationButton: PropTypes.shape({
        label: PropTypes.string,
        ariaLabel: PropTypes.string,
    }),
};

SearchContent.defaultProps = {
    paginationButton: {
        label: 'More Results',
        ariaLabel: 'View more search results',
    },
};

export default SearchContent;
